import React, { useEffect, useMemo, useState } from 'react';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import DsSwitch from '../../common/DsSwitch';
import { useToast } from '../../common/ToastSystem';
import { formatDateTimeJST } from '../../../utils/datetime';
import {
  getEventTicketSalesShareSettings,
  updateEventTicketSalesShareSettings,
  type TicketSalesShareSettings,
} from '../../../api/taskEvents';
import { ApiStatusError } from '../../../api/templates';
import '../../../css/admin/events/TicketSalesArtistShare.css';

type Props = {
  eventUuid: string;
  canShare: boolean;
  PersonAvatar: React.ComponentType<Record<string, unknown>>;
};

export default function TicketSalesArtistShareSettingsModal({ eventUuid, canShare, PersonAvatar }: Props) {
  const toast = useToast() as { success: (text: string) => void; error: (text: string) => void };
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<TicketSalesShareSettings | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [audienceMode, setAudienceMode] = useState<'all_participants' | 'selected'>('all_participants');
  const [selectedArtistIds, setSelectedArtistIds] = useState<string[]>([]);
  const [notifyOnUpdate, setNotifyOnUpdate] = useState(false);
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const next = await getEventTicketSalesShareSettings(eventUuid);
      setSettings(next);
      setEnabled(next.enabled);
      setAudienceMode(next.audienceMode);
      setSelectedArtistIds(next.selectedArtistIds);
      setNotifyOnUpdate(next.notifyOnUpdate);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '共有設定を取得できませんでした');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setQuery('');
    void load();
    // eventUuid/open 変更時だけ再取得する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventUuid, open]);

  const participants = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('ja');
    if (!normalized) return settings?.eligibleParticipants || [];
    return (settings?.eligibleParticipants || []).filter((participant) =>
      `${participant.name} ${participant.nameEn || ''}`.toLocaleLowerCase('ja').includes(normalized)
    );
  }, [query, settings?.eligibleParticipants]);

  const selectedCount = audienceMode === 'all_participants'
    ? settings?.eligibleParticipants.length || 0
    : selectedArtistIds.length;

  const save = async () => {
    setSaving(true);
    try {
      const next = await updateEventTicketSalesShareSettings(eventUuid, {
        enabled,
        audienceMode,
        selectedArtistIds: audienceMode === 'selected' ? selectedArtistIds : [],
        notifyOnUpdate,
        expectedUpdatedAt: settings?.updatedAt || null,
      });
      setSettings(next);
      toast.success('券売状況の共有設定を保存しました');
      setOpen(false);
    } catch (error) {
      if (error instanceof ApiStatusError && error.status === 409) {
        toast.error('別の管理者が設定を更新しました。最新の内容を読み込みます。');
        await load();
      } else {
        toast.error(error instanceof Error ? error.message : '共有設定を保存できませんでした');
      }
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <DsButton variant="secondary" onClick={() => setOpen(false)} disabled={saving}>キャンセル</DsButton>
      <DsButton
        onClick={save}
        loading={saving}
        disabled={loading || (enabled && audienceMode === 'selected' && selectedArtistIds.length === 0)}
      >
        保存
      </DsButton>
    </>
  );

  return (
    <>
      <DsButton
        variant="secondary"
        onClick={() => setOpen(true)}
        disabled={!canShare}
        title={!canShare ? 'このユーザー権限では券売状況を共有できません。' : undefined}
      >
        アーティストへの共有
      </DsButton>
      <DsModal
        open={open}
        onClose={() => setOpen(false)}
        title="券売状況をアーティストへ共有"
        ariaLabel="券売状況をアーティストへ共有"
        size="lg"
        footer={footer}
        dismissible={!saving}
        bodyClassName="ticket-sales-share-modal"
      >
        {loading || !settings ? (
          <p className="ticket-sales-share-modal__loading" role="status">共有設定を読み込んでいます。</p>
        ) : (
          <div className="ticket-sales-share-modal__body">
            <div className="ticket-sales-share-modal__switch-row">
              <div>
                <strong>券売数を共有</strong>
                <p>共有するのは販売枚数・残り枚数・販売率だけです。売上金額や手数料は表示しません。</p>
              </div>
              <DsSwitch
                id="ticket-sales-share-enabled"
                name="ticket-sales-share-enabled"
                checked={enabled}
                onChange={setEnabled}
                onError={undefined}
                label={null}
                ariaLabel="券売数を共有"
              />
            </div>

            <fieldset disabled={!enabled} className="ticket-sales-share-modal__audience">
              <legend>共有する人</legend>
              <label>
                <input
                  type="radio"
                  name="ticket-sales-share-audience"
                  checked={audienceMode === 'all_participants'}
                  onChange={() => setAudienceMode('all_participants')}
                />
                <span><strong>参加アーティスト全員</strong><small>契約済みと、契約なしで追加された参加者を含みます。</small></span>
              </label>
              <label>
                <input
                  type="radio"
                  name="ticket-sales-share-audience"
                  checked={audienceMode === 'selected'}
                  onChange={() => setAudienceMode('selected')}
                />
                <span><strong>共有する人を個別に選ぶ</strong><small>現在の参加アーティストから選択します。</small></span>
              </label>
            </fieldset>

            {enabled && audienceMode === 'selected' ? (
              <section className="ticket-sales-share-modal__participants" aria-labelledby="ticket-share-participant-heading">
                <div className="ticket-sales-share-modal__participants-heading">
                  <div>
                    <h3 id="ticket-share-participant-heading">共有対象</h3>
                    <span>{selectedCount}人選択中</span>
                  </div>
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="アーティスト名で検索"
                    aria-label="共有するアーティストを検索"
                  />
                </div>
                <div className="ticket-sales-share-modal__participant-list">
                  {participants.map((participant) => {
                    const checked = selectedArtistIds.includes(participant.artistId);
                    return (
                      <label key={participant.artistId}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) => setSelectedArtistIds((current) => event.target.checked
                            ? [...new Set([...current, participant.artistId])]
                            : current.filter((artistId) => artistId !== participant.artistId))}
                        />
                        <PersonAvatar
                          role="artist"
                          name={participant.name}
                          profilePicture={participant.profilePicture}
                          size={36}
                          ariaHidden
                        />
                        <span><strong>{participant.name}</strong><small>{participant.participationSources.includes('manual') ? '契約なしで追加' : '契約参加'}</small></span>
                      </label>
                    );
                  })}
                  {!participants.length ? <p>該当する参加アーティストはいません。</p> : null}
                </div>
              </section>
            ) : null}

            <div className="ticket-sales-share-modal__switch-row">
              <div>
                <strong>更新を通知</strong>
                <p>更新があった翌日の朝8:00に、対象者へアナウンス・メール・PUSHで通知します。LINE通知は送りません。</p>
              </div>
              <DsSwitch
                id="ticket-sales-share-notify"
                name="ticket-sales-share-notify"
                checked={notifyOnUpdate}
                onChange={setNotifyOnUpdate}
                onError={undefined}
                disabled={!enabled}
                label={null}
                ariaLabel="券売状況の更新を通知"
              />
            </div>

            <dl className="ticket-sales-share-modal__meta">
              <div><dt>現在の共有対象</dt><dd>{enabled ? `${selectedCount}人` : '共有しない'}</dd></div>
              <div><dt>最終通知</dt><dd>{formatDateTimeJST(settings.lastPublishedAt) || 'まだありません'}</dd></div>
            </dl>
          </div>
        )}
      </DsModal>
    </>
  );
}
